// File: frontend/src/pages/Capabilities/CapabilitiesV2.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSEO } from '../../contexts/SEOContext';
import { Helmet } from 'react-helmet-async';
import LandingButton from '../../components/Button/LandingButton';
import PricingCard from './components/PricingCard';
import DashboardCards from './components/DashboardCards';
import CategoryTabs from './components/CategoryTabs';
import CapabilityMatrix from './components/CapabilityMatrix';
import VideoShowcase from './components/VideoShowcase';
import styles from './CapabilitiesV2.module.css';
import capabilitiesData from './capabilitiesData.json';

const CapabilitiesV2 = () => {
  const { t, language, supportedLanguages } = useLanguage();
  const { updateSEO } = useSEO();
  const [selectedIndustry, setSelectedIndustry] = useState('Safe Superintelligence');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const matrixRef = useRef(null);
  
  const industries = useMemo(() => ['All', ...capabilitiesData.industries], []);
  const baseUrl = 'https://allegory.app';
  const currentPath = '/capabilities';

  // Filter capabilities based on selected industry
  const filteredData = useMemo(() => {
    return capabilitiesData.capabilities.filter(item => 
      selectedIndustry === 'All' || item.industry === selectedIndustry
    );
  }, [selectedIndustry]);

  // Transform data for the capability matrix
  const matrixData = useMemo(() => {
    // Group by category
    const categoriesMap = {};
    
    filteredData.forEach(item => {
      if (!categoriesMap[item.category]) {
        categoriesMap[item.category] = {
          name: t(`categories.${item.category}`, 'capabilities'),
          features: []
        };
      }
      
      categoriesMap[item.category].features.push({
        name: t(`capabilities.${item.id}`, 'capabilities'),
        ai1: item.ai1 !== "Not Available",
        ai2: item.ai2 !== "Not Available",
        ai3: item.ai3 !== "Not Available",
        ai4: item.ai4 !== "Not Available",
        details: {
          ai1: item.ai1,
          ai2: item.ai2,
          ai3: item.ai3,
          ai4: item.ai4
        }
      });
    });
    
    return Object.values(categoriesMap);
  }, [filteredData, t]);

  // Pricing data
  const pricingOptions = [
    {
      id: 'distribution',
      title: t('capabilities.pricing.distribution.title'),
      price: t('capabilities.pricing.distribution.price'),
      description: t('capabilities.pricing.distribution.description'),
      features: [
        t('capabilities.pricing.distribution.features.f1'),
        t('capabilities.pricing.distribution.features.f2'),
        t('capabilities.pricing.distribution.features.f3'),
        t('capabilities.pricing.distribution.features.f4'),
        t('capabilities.pricing.distribution.features.f5'),
        t('capabilities.pricing.distribution.features.f6'),
      ],
      cta: t('capabilities.pricing.distribution.cta', 'Become a Partner'),
      ctaLink: '/contact',
      highlight: false
    },
    {
      id: 'productivity',
      title: t('capabilities.pricing.productivity.title', 'Productivity Tools'),
      price: t('capabilities.pricing.productivity.price', 'Starting from $299/month'),
      description: t('capabilities.pricing.productivity.description', 'Unlock AI agents that will boost your productivity'),
      features: [
        t('capabilities.pricing.productivity.features.f1'),
        t('capabilities.pricing.productivity.features.f2'),
        t('capabilities.pricing.productivity.features.f3'),
        t('capabilities.pricing.productivity.features.f4'),
        t('capabilities.pricing.productivity.features.f5'),
        t('capabilities.pricing.productivity.features.f6'),
      ],
      cta: t('capabilities.pricing.productivity.cta', 'Get Started'),
      ctaLink: '/sign-up',
      highlight: true
    },
    {
      id: 'infrastructure',
      title: t('capabilities.pricing.infrastructure.title'),
      price: t('capabilities.pricing.infrastructure.price'),
      description: t('capabilities.pricing.infrastructure.description'),
      features: [
        t('capabilities.pricing.infrastructure.features.f1'),
        t('capabilities.pricing.infrastructure.features.f2'),
        t('capabilities.pricing.infrastructure.features.f3'),
        t('capabilities.pricing.infrastructure.features.f4'),
        t('capabilities.pricing.infrastructure.features.f5'),
        t('capabilities.pricing.infrastructure.features.f6'),
      ],
      cta: t('capabilities.pricing.infrastructure.cta'),
      ctaLink: '/contact',
      highlight: false
    }
  ];

  // Video showcase data
  const videoItems = [
    {
      id: 'ai1',
      title: 'Allegory Intelligence AI-1 - 3:34',
      src: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-products-ai1.mp4',
      poster: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-products-ai1-poster.jpg'
    },
    {
      id: 'embedded',
      title: 'Embedded Insurance - 3:04',
      src: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-products-embedded-insurance.mp4',
      poster: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-products-embedded-insurance-poster.jpg'
    },
    {
      id: 'qms',
      title: 'Quote Management System - 2:03',
      src: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-products-qms.mp4',
      poster: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-products-qms-poster.jpg'
    },
    {
      id: 'auto',
      title: 'Auto Insight - 0:57',
      src: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-products-auto-insight.mp4',
      poster: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-products-auto-insight-poster.jpg'
    },
    {
      id: 'property',
      title: 'Property Insight - 2:15',
      src: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-products-property-insight.mp4',
      poster: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-products-property-insight-poster.jpg'
    },
    {
      id: 'sales',
      title: 'AI-1 Powered Insurance Sales - 1:07',
      src: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-sales-desktop.mp4',
      poster: 'https://allegory-public.s3.ca-central-1.amazonaws.com/pitch/pitch-sales-desktop-poster.jpg'
    }
  ];

  // Handle video modal
  const handleOpenVideo = (video) => {
    setSelectedVideo(video);
    setShowVideoModal(true);
  };

  const handleCloseVideo = () => {
    setShowVideoModal(false);
    setSelectedVideo(null);
  };

  // Scroll to matrix
  const scrollToMatrix = () => {
    if (matrixRef.current) {
      matrixRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // SEO setup
  useEffect(() => {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'TechArticle',
      headline: t('capabilities_title'),
      description: t('capabilities_description'),
      keywords: t('capabilities_keywords'),
      datePublished: '2024-10-28',
      dateModified: new Date().toISOString(),
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${baseUrl}${currentPath}`
      },
      author: {
        '@type': 'Organization',
        name: 'Allegory Insurance Technologies',
        url: baseUrl
      },
      about: {
        '@type': 'SoftwareApplication',
        name: 'Allegory AI Platform',
        applicationCategory: 'Insurance Software',
        operatingSystem: 'Web-based',
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/OnlineOnly'
        },
        featureList: filteredData.map(item => t(`capabilities.${item.id}`)).join(', ')
      }
    };

    updateSEO({
      title: t('capabilities_title'),
      description: t('capabilities_description'),
      keywords: t('capabilities_keywords'),
      canonicalUrl: `${baseUrl}/${language}${currentPath}`,
      ogImage: `${baseUrl}/images/capabilities-og.jpg`,
      structuredData
    });
  }, [t, language, updateSEO, filteredData]);

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{t('capabilities_title')} | Allegory</title>
        <meta name="description" content={t('capabilities_description')} />
        <meta name="keywords" content={t('capabilities_keywords')} />
        
        {/* Language alternates */}
        <link rel="canonical" href={`${baseUrl}/${language}${currentPath}`} />
        {supportedLanguages.map(lang => (
          <link 
            key={lang}
            rel="alternate" 
            hreflang={lang} 
            href={`${baseUrl}/${lang}${currentPath}`}
          />
        ))}
        <link 
          rel="alternate" 
          hreflang="x-default" 
          href={`${baseUrl}/en-ca${currentPath}`}
        />

        {/* Open Graph */}
        <meta property="og:title" content={t('capabilities_title')} />
        <meta property="og:description" content={t('capabilities_description')} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${baseUrl}/${language}${currentPath}`} />
        <meta property="og:locale" content={language} />
        <meta property="article:modified_time" content={new Date().toISOString()} />
      </Helmet>

      <div className={styles.wrapper}>
        <div className={styles.container}>
          {/* Hero Section - FIXED: Background elements moved inside the motion.div */}
          <motion.div 
            className={styles.hero}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Background elements */}
            <div className={styles.heroBgElement1} />
            <div className={styles.heroBgElement2} />
            <div className={styles.heroBgElement3} />
            
            <h1 className={styles.title}>{t('capabilities_title_extended_v2')}</h1>
            <p className={styles.subtitle}>{t('capabilities_description_v2')}</p>
            <LandingButton 
              onClick={scrollToMatrix}
              className={styles.exploreButton}
            >
              {t('capabilities.explore_capabilities', 'Explore Capabilities')}
            </LandingButton>
          </motion.div>

          {/* Video Showcase */}
          <section className={styles.videoSection}>
            <h2 className={styles.sectionTitle}>
                {t('capabilities.video_showcase.title', 'See Our Platform in Action')}
            </h2>
            <p className={styles.sectionSubtitle}>
                {t('capabilities.video_showcase.subtitle', 'Watch demos of our key features and capabilities')}
            </p>
            
            {/* Add decorative elements */}
            <div className={styles.videoSectionDecoration1} />
            <div className={styles.videoSectionDecoration2} />
            
            <VideoShowcase 
                videos={videoItems} 
                onOpenVideo={handleOpenVideo}
            />
            </section>

          <div className={styles.sectionDivider} />

          {/* Pricing Section */}
          <section className={styles.pricingSection}>
            <h2 className={styles.sectionTitle}>
              {t('capabilities.pricing.title', 'Flexible Pricing Options')}
            </h2>
            <p className={styles.sectionSubtitle}>
              {t('capabilities.pricing.subtitle', 'Choose the plan that fits your business needs')}
            </p>
            <div className={styles.pricingGrid}>
              {pricingOptions.map((option) => (
                <PricingCard 
                  key={option.id}
                  {...option}
                />
              ))}
            </div>
          </section>

          <div className={styles.sectionDivider} />

          {/* Dashboard Cards Section */}
          <DashboardCards />
          
          <div className={styles.sectionDivider} />

          {/* Capabilities Matrix Section */}
          <section className={styles.matrixSection} ref={matrixRef}>
            <h2 className={styles.sectionTitle}>
              {t('capabilities.matrix.title', 'Comprehensive Capabilities')}
            </h2>
            <p className={styles.sectionSubtitle}>
              {t('capabilities.matrix.subtitle', 'Explore our full range of capabilities across different industries')}
            </p>
            
            {/* Category Tabs */}
            <CategoryTabs 
              categories={industries.map(industry => ({
                id: industry,
                title: industry === 'All' 
                  ? t('all_industries')
                  : t(`${industry}`)
              }))} 
              currentCategoryIndex={industries.indexOf(selectedIndustry)}
              onCategoryChange={(index) => setSelectedIndustry(industries[index])}
            />
            
            {/* Capability Matrix */}
            <CapabilityMatrix categories={matrixData} />
          </section>
          
          <div className={styles.sectionDivider} />
          
          {/* CTA Section */}
          <section className={styles.ctaSection}>
            <div className={styles.ctaContent}>
              <h2 className={styles.ctaTitle}>
                {t('capabilities.cta.title')}
              </h2>
              <p className={styles.ctaDescription}>
                {t('capabilities.cta.description')}
              </p>
              <div className={styles.ctaButtons}>
                <LandingButton 
                  href="/contact" 
                  className={styles.primaryCta}
                >
                  {t('capabilities.cta.primary', 'Contact Sales')}
                </LandingButton>
                <LandingButton 
                  href="/sign-up" 
                  className={styles.secondaryCta}
                  variant="secondary"
                >
                  {t('capabilities.cta.secondary', 'Start Free Trial')}
                </LandingButton>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && selectedVideo && (
            <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseVideo}
            >
            <motion.div
                className={styles.modalContent}
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>{selectedVideo.title}</h3>
                <button 
                    className={styles.closeButton}
                    onClick={handleCloseVideo}
                    aria-label="Close video"
                >
                    <span className={styles.closeIcon}>×</span>
                </button>
                </div>
                <div className={styles.videoContainer}>
                <video
                    className={styles.video}
                    src={selectedVideo.src}
                    poster={selectedVideo.poster}
                    controls
                    autoPlay
                    playsInline
                />
                </div>
            </motion.div>
            </motion.div>
        )}
        </AnimatePresence>
    </>
  );
};

export default CapabilitiesV2;